import React from 'react'
import { toast } from 'sonner'
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { PartnerService } from '@duck/api-definitions'
import {
  EllipsisVerticalIcon,
  LinkIcon,
  PencilIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

interface AssignmentActionsDropdownProps {
  isActive: boolean
  onEdit: () => void
  onDeleteClick: () => void
  onSetStatus: (status: PartnerService.AssignmentStatus) => void
  /** When set (public assignments), a "Copy link" item is shown first. */
  linkToken?: string
}

export const AssignmentActionsDropdown: React.FC<AssignmentActionsDropdownProps> = ({
  isActive,
  onEdit,
  onDeleteClick,
  onSetStatus,
  linkToken,
}) => {
  const { t } = useTar()

  const copyLink = async () => {
    if (!linkToken) return
    const url = `${window.location.origin}/?atoken=${linkToken}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('common.linkCopied'))
    } catch {
      toast.error(t('common.failedToCopyLink') + '\n' + url, { duration: 15000 })
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
          <EllipsisVerticalIcon className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {linkToken && (
          <DropdownMenuItem onClick={copyLink}>
            <LinkIcon className="h-4 w-4" />
            {t('partner.copyPublicLink')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onEdit}>
          <PencilIcon className="h-4 w-4" />
          {t('common.edit')}
        </DropdownMenuItem>
        {isActive ? (
          <DropdownMenuItem onClick={() => onSetStatus(PartnerService.AssignmentStatus.Inactive)}>
            <PauseIcon className="h-4 w-4" />
            {t('partner.deactivate')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => onSetStatus(PartnerService.AssignmentStatus.Active)}>
            <PlayIcon className="h-4 w-4" />
            {t('partner.activate')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onDeleteClick} className="text-destructive focus:text-destructive">
          <TrashIcon className="h-4 w-4" />
          {t('common.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
